import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';

import { initials } from './initials';

interface AvatarProps {
  /** The person's full name — the initials are derived from it. */
  name: string;
  /** The photo's resolved URL, when there is one. */
  src?: string | null;
  /** `xl` for the ficha header, `md` for list rows. */
  size?: 'md' | 'xl';
  /**
   * Announced by screen readers in place of the photo. The default ("Foto de
   * {name}") would be redundant with the `<img alt>`, so a decorative avatar
   * passes an empty string instead.
   */
  label?: string;
  className?: string;
}

const SIZES = {
  md: 'size-10 text-sm',
  xl: 'size-20 text-xl',
} as const;

/**
 * Circular avatar: the photo when there is one, the initials otherwise.
 *
 * A broken image URL falls back to the initials too — the failure is remembered
 * per URL, so a dead signed URL shows a person rather than a broken-image glyph,
 * while a *different* src (a re-minted URL, a photo replaced after an upload)
 * gets a fresh attempt. The initials are the real content, not decoration, so
 * they always render and the photo is layered over them.
 */
export function Avatar({ name, src, size = 'md', label, className }: AvatarProps) {
  const { t } = useTranslation('common');
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const photo = src && failedSrc !== src ? src : null;

  return (
    <span
      className={cn(
        'bg-surface-sunken text-ink-muted border-border relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border font-medium',
        SIZES[size],
        className,
      )}
    >
      <span aria-hidden="true">{initials(name)}</span>
      {photo && (
        <img
          src={photo}
          alt={label ?? t('common:avatar.alt', { name })}
          className="absolute inset-0 size-full object-cover"
          onError={() => setFailedSrc(photo)}
        />
      )}
    </span>
  );
}
